import type { BoardConfig } from "./api"
import { prioritiesOf } from "./boardConfig"

// How the board orders cards inside each column, and how the list orders rows.
//
// The board opens sorted by creation date, newest last, so a project reads as
// the story of how it grew. Priority is opt-in: most tasks carry "none" until
// someone decides otherwise, and a board sorted by it opens as one heap.
// "manual" is the drag order the server stores in `tasks.order` — the sort
// control is what returns you to it, because dragging is off while a field
// sort is active.

export type BoardSort = "created" | "priority" | "manual"

/** The URL's word for the sort, defaulting to created. Unknown values read as
 *  the default rather than failing, so an old link keeps working. */
export function readBoardSort(value: string | null | undefined): BoardSort {
  return value === "priority" || value === "manual" ? value : "created"
}

type Sortable = { createdAt: string; priority: string }

/** Rank of a priority: its position in the board's own list, highest first.
 *  A priority the config no longer knows sorts after every known one. */
function priorityRank(priorities: { id: string }[]) {
  const rank = new Map(priorities.map((priority, index) => [priority.id, index]))
  return (id: string) => rank.get(id) ?? priorities.length
}

function comparator(sort: Exclude<BoardSort, "manual">, priorities: { id: string }[]) {
  if (sort === "created") {
    // Newest last: oldest work first, like a changelog read downwards.
    return (a: Sortable, b: Sortable) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
  }
  const rank = priorityRank(priorities)
  return (a: Sortable, b: Sortable) => rank(a.priority) - rank(b.priority)
}

/** Stable: rows that tie keep the order they arrived in, so a re-render never
 *  shuffles cards that compare equal. */
function stableSort<T>(rows: T[], compare: (a: T, b: T) => number): T[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => compare(a.row, b.row) || a.index - b.index)
    .map(({ row }) => row)
}

/** The board's columns: one flat order for every card. Manual returns the
 *  array untouched, the reference included, so nothing downstream re-renders. */
export function sortBoardTasks<T extends Sortable>(
  tasks: T[],
  sort: BoardSort,
  config: BoardConfig,
): T[] {
  if (sort === "manual") return tasks
  return stableSort(tasks, comparator(sort, prioritiesOf(config)))
}

/** The list: children stay under their parents, and only siblings are sorted.
 *  Same shape as nestChildren, which reads the result without moving anything:
 *  a sorted tree is already in the order it would have produced. */
export function sortTaskTree<T extends Sortable & { id: string; parentTaskId: string | null }>(
  tasks: T[],
  sort: BoardSort,
  config: BoardConfig,
): T[] {
  if (sort === "manual") return tasks
  const compare = comparator(sort, prioritiesOf(config))
  const present = new Set(tasks.map((task) => task.id))
  const childrenOf = new Map<string, T[]>()
  for (const task of tasks) {
    if (task.parentTaskId && present.has(task.parentTaskId)) {
      const list = childrenOf.get(task.parentTaskId) ?? []
      list.push(task)
      childrenOf.set(task.parentTaskId, list)
    }
  }

  const out: T[] = []
  const seen = new Set<string>()
  const visit = (task: T) => {
    if (seen.has(task.id)) return
    seen.add(task.id)
    out.push(task)
    for (const child of stableSort(childrenOf.get(task.id) ?? [], compare)) visit(child)
  }
  const roots = tasks.filter((task) => !(task.parentTaskId && present.has(task.parentTaskId)))
  for (const root of stableSort(roots, compare)) visit(root)
  // A parent cycle can only come from bad data; never lose a row over it.
  for (const task of tasks) if (!seen.has(task.id)) visit(task)
  return out
}
